import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import UserCombobox from '../../../util_components/user_combobox/UserCombobox';
import {
  add_sales_member,
  api_error_reason,
  api_status,
  fetch_sales_members,
  remove_sales_member
} from './api';
import type { RoleMembers, RoleUser } from './api';
import { can_remove, display_name, filter_candidates, sort_members } from './sales_users';

export type Notify = (type: 'success' | 'error', message: string) => void;

interface Props {
  notify: Notify;
  self_uuid?: string;
}

const MUTED_STYLE: React.CSSProperties = { color: 'var(--text-muted)' };

// Inline styles beat the host CMS's global `button {}` rule.
const PRIMARY_BTN: React.CSSProperties = {
  background: 'var(--brand-primary)',
  color: 'var(--text-on-brand)',
  border: 'none',
  margin: 0
};
const SECONDARY_BTN: React.CSSProperties = {
  background: 'var(--surface-card)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-default)',
  margin: 0
};

interface LoadError {
  forbidden: boolean;
  reason: string;
}

function SalesUsersPanel({ notify, self_uuid = '' }: Props) {
  const [data, set_data] = useState<RoleMembers | null>(null);
  const [loading, set_loading] = useState(true);
  const [load_error, set_load_error] = useState<LoadError | null>(null);
  const [selected, set_selected] = useState('');
  const [busy, set_busy] = useState<string | null>(null);

  const load = useCallback(() => {
    set_loading(true);
    return fetch_sales_members()
      .then((d) => {
        set_data(d);
        set_load_error(null);
      })
      .catch((e: unknown) => {
        const forbidden = api_status(e) === 403;
        const reason = forbidden
          ? 'Super user only'
          : api_error_reason(e, 'Failed to load sales users');
        set_load_error({ forbidden, reason });
        if (!forbidden) notify('error', reason);
      })
      .finally(() => set_loading(false));
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const members = sort_members(data?.members);
  const options = filter_candidates(data?.candidates, '', data?.members);
  const chosen = options.find((c) => c.uuid === selected) || null;

  const on_add = () => {
    if (!chosen || busy) return;
    set_busy(chosen.uuid);
    add_sales_member(chosen.uuid)
      .then(() => {
        notify('success', `Added ${display_name(chosen)} to SALES`);
        set_selected('');
        return load();
      })
      .catch((e: unknown) => notify('error', api_error_reason(e, 'Failed to add user')))
      .finally(() => set_busy(null));
  };

  const on_remove = (m: RoleUser) => {
    if (busy || !can_remove(m, self_uuid).ok) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the SALES role from ${display_name(m)}?`)) return;
    set_busy(m.uuid);
    remove_sales_member(m.uuid)
      .then(() => {
        notify('success', `Removed ${display_name(m)} from SALES`);
        return load();
      })
      .catch((e: unknown) => notify('error', api_error_reason(e, 'Failed to remove user')))
      .finally(() => set_busy(null));
  };

  const render_row = (m: RoleUser) => {
    const check = can_remove(m, self_uuid);
    return (
      <tr key={m.uuid} className="border-t border-line">
        <td className="px-4 py-3 text-left">
          <span className="font-medium">{display_name(m)}</span>
          {m.is_active ? null : (
            <span className="ml-2 inline-block rounded-full bg-surface-sunken px-2 py-0.5 text-xs font-medium text-content-muted">
              Inactive
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-left" style={MUTED_STYLE}>
          {m.email || '—'}
        </td>
        <td className="px-4 py-3 text-left">
          <span title={check.hint}>
            <button
              type="button"
              onClick={() => on_remove(m)}
              disabled={!check.ok || !!busy}
              aria-label={`Remove ${display_name(m)}`}
              className="rounded-md px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
              style={SECONDARY_BTN}
            >
              {busy === m.uuid ? 'Removing…' : 'Remove'}
            </button>
          </span>
          {check.hint ? (
            <div className="mt-1 text-xs" style={MUTED_STYLE}>
              {check.hint}
            </div>
          ) : null}
        </td>
      </tr>
    );
  };

  const render_body = () => {
    if (load_error && !data) {
      return (
        <tr>
          <td colSpan={3} className="px-4 py-8 text-center">
            <div role="alert" style={{ color: 'var(--state-error)' }}>
              {load_error.forbidden
                ? 'Super user only'
                : `Could not load sales users: ${load_error.reason}`}
            </div>
            {load_error.forbidden ? null : (
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="mt-3 rounded-md px-3 py-1.5 text-xs font-medium"
                style={PRIMARY_BTN}
              >
                {loading ? 'Retrying…' : 'Retry'}
              </button>
            )}
          </td>
        </tr>
      );
    }
    if (loading && !data) {
      return (
        <tr>
          <td colSpan={3} className="px-4 py-8 text-center" style={MUTED_STYLE}>
            Loading sales users…
          </td>
        </tr>
      );
    }
    if (members.length === 0) {
      return (
        <tr>
          <td colSpan={3} className="px-4 py-8 text-center" style={MUTED_STYLE}>
            No users have the SALES role yet.
          </td>
        </tr>
      );
    }
    return members.map(render_row);
  };

  return (
    <section
      className="bg-surface-card rounded-lg shadow-sm border border-line"
      style={{ margin: '20px 5%' }}
    >
      <div className="px-5 py-3 border-b border-line">
        <h2 className="font-semibold">Sales users</h2>
        <p className="mt-1 text-xs" style={{ ...MUTED_STYLE, margin: 0 }}>
          Users with the SALES role get sales task pop-ups and can claim tasks. Adding here is the
          same as adding the SALES role on CMS Users. Level and shift are set on the{' '}
          <Link to="/sales-team" style={{ color: 'var(--brand-primary)' }}>
            Sales Team page
          </Link>
          .
        </p>
      </div>
      {data ? (
        <div className="px-5 py-3 flex flex-wrap items-center gap-3 border-b border-line">
          <UserCombobox
            id="sales-users-candidate"
            label="User to add"
            users={options}
            value={selected}
            on_change={set_selected}
            disabled={!!busy}
          />
          <button
            type="button"
            onClick={on_add}
            disabled={!chosen || !!busy}
            className="rounded-md px-4 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
            style={PRIMARY_BTN}
          >
            {busy && chosen && busy === chosen.uuid ? 'Adding…' : 'Add user'}
          </button>
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-sunken text-content-secondary">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Name</th>
              <th className="px-4 py-2 text-left font-medium">Email</th>
              <th className="px-4 py-2 text-left font-medium">Action</th>
            </tr>
          </thead>
          <tbody>{render_body()}</tbody>
        </table>
      </div>
    </section>
  );
}

export default SalesUsersPanel;
